import { useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { useAuth } from '../auth/useAuth'
import { brandAssetUrl } from '@/runtime'
import { cn } from '@/lib/utils'
import styles from './LoginPage.module.scss'

function buildRedirectUrl(next: string | null): string {
  // Only allow same-origin paths starting with "/" — protects against open
  // redirect via a poisoned ?next= query param.
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/'
  return `${window.location.origin}${safeNext}`
}

export function LoginPage() {
  const { requestMagicLink } = useAuth()
  const location = useLocation()
  const next = new URLSearchParams(location.search).get('next')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const [devLink, setDevLink] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const cleanEmail = email.trim()
    if (!cleanEmail) return
    setStatus('sending')
    setMessage(null)
    setDevLink(null)
    try {
      const payload = await requestMagicLink(cleanEmail, buildRedirectUrl(next))
      setStatus('sent')
      setMessage('Controlla la tua email per il link di accesso.')
      if (payload.login_url) setDevLink(payload.login_url)
    } catch (error: unknown) {
      setStatus('error')
      setMessage(error instanceof Error ? error.message : 'Impossibile richiedere un link di accesso')
    }
  }

  return (
    <main className={styles.page} data-bank="credem">
      <section className={styles.card} aria-labelledby="login-title">
        <div className={styles.brand}>
          <div className={styles.logo}>
            <img src={brandAssetUrl('credem-lockup.png')} alt="Credem" draggable={false} />
          </div>
          <div>
            <h1 id="login-title" className={styles.title}>
              Credem
            </h1>
            <p className={styles.subtitle}>Accedi con la tua email aziendale.</p>
          </div>
        </div>
        <form className={styles.form} onSubmit={handleSubmit}>
          <label className={styles.label}>
            <span className={styles.labelText}>Email</span>
            <input
              autoComplete="email"
              className={styles.input}
              inputMode="email"
              name="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              required
              type="email"
              value={email}
            />
          </label>
          <Button disabled={status === 'sending'} type="submit">
            {status === 'sending' ? 'Invio in corso...' : 'Invia link di accesso'}
          </Button>
        </form>
        {message ? (
          <p className={cn(styles.message, status === 'error' && styles.messageError)}>{message}</p>
        ) : null}
        {devLink ? (
          <a className={styles.devLink} href={devLink}>
            Apri link locale di accesso
          </a>
        ) : null}
      </section>
    </main>
  )
}
